import React, { useState, useEffect } from 'react';
import { Star, User, Calendar, MapPin, ExternalLink, AlertTriangle, TrendingDown, Info, Brain, Sparkles } from 'lucide-react';

function GoogleReviews({ address }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [placeInfo, setPlaceInfo] = useState(null);
  const [error, setError] = useState(null);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    if (!address || !window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps Places API not available');
      return;
    }

    setLoading(true);
    setError(null);
    setReviews([]);
    setPlaceInfo(null);

    const service = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );

    // Simple timeout
    const searchTimeout = setTimeout(() => {
      console.log('Search timeout reached');
      setLoading(false);
      setError('Search timeout');
    }, 5000);

    // Super simple search: "parking near [address]"
    const searchQuery = `parking near ${address}`;
    console.log('Searching for:', searchQuery);

    const request = {
      query: searchQuery,
      fields: ['place_id', 'name', 'rating', 'user_ratings_total', 'formatted_address']
    };

    service.findPlaceFromQuery(request, (results, status) => {
      console.log('Search status:', status, 'Results:', results?.length || 0);
      
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
        // Just take the first result
        const place = results[0];
        console.log('Found place:', place.name);
        
        // Get details for the first result
        const detailsRequest = {
          placeId: place.place_id,
          fields: ['name', 'rating', 'user_ratings_total', 'reviews', 'formatted_address', 'types']
        };

        service.getDetails(detailsRequest, (placeDetails, detailsStatus) => {
          clearTimeout(searchTimeout);
          
          if (detailsStatus === window.google.maps.places.PlacesServiceStatus.OK && placeDetails) {
            console.log('Got details for:', placeDetails.name);
            
            setPlaceInfo({
              name: placeDetails.name,
              rating: placeDetails.rating,
              totalReviews: placeDetails.user_ratings_total,
              address: placeDetails.formatted_address,
              types: placeDetails.types
            });

            if (placeDetails.reviews && placeDetails.reviews.length > 0) {
              const filteredReviews = placeDetails.reviews
                .filter(review => review.rating && review.text && review.text.length > 10)
                .sort((a, b) => b.time - a.time);

              console.log('Found reviews:', filteredReviews.length);
              setReviews(filteredReviews);
              
              // Analyze reviews for common issues
              if (filteredReviews.length > 0) {
                analyzeReviews(filteredReviews);
              }
            } else {
              console.log('No reviews found');
              setReviews([]);
            }
          } else {
            console.log('Failed to get place details');
            setError('Failed to get place details');
          }
          
          setLoading(false);
        });
      } else {
        console.log('No results found');
        clearTimeout(searchTimeout);
        setLoading(false);
        setError('No parking found near this address');
      }
    });

    // Cleanup
    return () => {
      clearTimeout(searchTimeout);
    };
  }, [address]);

  const analyzeReviews = async (reviewsData) => {
    setSummaryLoading(true);
    try {
      const reviewTexts = reviewsData.map(r => r.text).join('\n\n');
      console.log('Review texts being sent to GPT:', reviewTexts);
      console.log('Number of reviews:', reviewsData.length);
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer 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'
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [{
            role: 'user',
            content: `Analyze these parking reviews and identify the top issue that drivers mention that could be solved with better parking management technology (like payment systems, enforcement, space availability, clear signage, etc.). Focus on operational/tech-solvable problems, not structural issues. Be very concise. Format as JSON:
            {
              "topIssue": "Brief description of the main solvable issue",
              "howTechHelps": "How parking management technology could solve this",
              "severity": "high/medium/low"
            }
            
            Reviews:
            ${reviewTexts}`
          }],
          max_tokens: 200,
          temperature: 0.3
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('GPT response:', data.choices[0].message.content);
        const analysis = JSON.parse(data.choices[0].message.content);
        console.log('Parsed analysis:', analysis);
        setReviewSummary(analysis);
      } else {
        console.error('GPT API error:', response.status, response.statusText);
        const errorData = await response.text();
        console.error('Error details:', errorData);
      }
    } catch (error) {
      console.error('Error analyzing reviews:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} size={16} fill="#fbbf24" color="#fbbf24" />);
    }

    if (hasHalfStar) {
      stars.push(<Star key="half" size={16} fill="#fbbf24" color="#fbbf24" style={{ clipPath: 'inset(0 50% 0 0)' }} />);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} size={16} color="#e5e7eb" />);
    }

    return stars;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short'
    });
  };

  const truncateText = (text, maxLength = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="card">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Customer Reviews</h2>
          <p className="text-gray-600 mb-8">See what drivers are saying about parking in this area</p>
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <div className="text-gray-500">Finding parking reviews near this location...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || reviews.length === 0) {
    return (
      <div className="card">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Customer Reviews</h2>
          <p className="text-gray-600 mb-8">See what drivers are saying about parking in this area</p>
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <MapPin size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Reviews Found</h3>
            <p className="text-gray-500 mb-2">We couldn't find customer reviews for parking near this address</p>
            <p className="text-sm text-gray-400 max-w-md">
              {placeInfo ? "Found parking facilities but no customer feedback is available yet" : "This area may not have public parking with online reviews"}
            </p>
          </div>
        </div>
      </div>
    );
  }

      return (
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Customer Reviews</h2>
            <p className="text-sm text-gray-600">{placeInfo?.name}</p>
          </div>
          {placeInfo && (
            <div className="text-right">
              <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center gap-1">
                  {renderStars(placeInfo.rating || 0)}
                </div>
                <span className="text-lg font-bold text-gray-800">{placeInfo.rating?.toFixed(1) || 'N/A'}</span>
              </div>
              <div className="text-xs text-gray-500">({placeInfo.totalReviews || 0} reviews)</div>
            </div>
          )}
        </div>

            {/* AI Analysis */}
      {(reviewSummary || summaryLoading) && (
        <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200 shadow-sm">
          {summaryLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-3"></div>
              <span className="text-gray-600">Analyzing reviews...</span>
            </div>
          ) : reviewSummary && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-800">🧠 Top Driver Issue</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">{reviewSummary.topIssue}</p>
            </div>
          )}
        </div>
      )}
      
             <div className="grid grid-cols-2 gap-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
         {reviews.map((review, index) => (
           <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
             <div className="mb-3">
               <div className="flex-1">
                 <div className="flex items-center justify-between mb-1">
                   <h4 className="text-sm font-medium text-gray-800 truncate">{review.author_name || 'Anonymous'}</h4>
                   <div className="flex items-center gap-1 flex-shrink-0">
                     {renderStars(review.rating)}
                   </div>
                 </div>
                 <div className="flex items-center gap-2 text-xs text-gray-500">
                   <Calendar size={10} />
                   <span>{formatDate(review.time)}</span>
                 </div>
               </div>
             </div>
             
             <div className="bg-gray-50 rounded p-3 border-l-2 border-blue-300">
               <p className="text-gray-700 leading-relaxed text-xs">
                 "{truncateText(review.text, 120)}"
               </p>
             </div>
           </div>
         ))}
       </div>
      
      
    </div>
  );
}

export default GoogleReviews; 